import { Link, useParams } from "react-router-dom";
import EquipmentSpecTable from "../components/equipment/EquipmentSpecTable";
import VendorLogo from "../components/equipment/VendorLogo";
import usePageMeta from "../seo/usePageMeta";
import { equipmentDetailJsonLd } from "../seo/equipmentSchema";
import { formatVerifiedDate } from "../seo/formatPublished";
import { SHOW_EQUIPMENT_CATALOG } from "../featureFlags";
import { ROBOTS } from "../data/robots";
import { specRows } from "../data/equipmentDisplay";
import { displaySpec } from "../data/equipmentIndex";
import {
  CONFIGURATION_DISCLAIMER,
  keyFactsText,
  publishedFaqs,
  relatedMachines,
  summaryDescription,
} from "../data/equipmentDetail";
import NotFound from "./NotFound";

function detailTitle(robot) {
  const full = `${robot.displayName} Specs and Integration | MAG`;
  if (full.length <= 60) return full;
  return `${robot.displayName} | MAG`;
}

const HERO_FIGURES = [
  { label: "Payload", value: (robot) => displaySpec(robot.payloadKg, "kg") },
  { label: "Top speed", value: (robot) => displaySpec(robot.maxSpeedMs, "m/s") },
  { label: "Lift height", value: (robot) => displaySpec(robot.liftHeightMm, "mm") },
  { label: "Navigation", value: (robot) => displaySpec(robot.navigation) },
  { label: "Minimum aisle", value: (robot) => displaySpec(robot.minAisleMm, "mm") },
];

function applicationLabel(item) {
  if (typeof item === "string") return item;
  if (item && typeof item.name === "string") return item.name;
  return "";
}

export default function EquipmentDetail() {
  const { slug } = useParams();
  const robot = slug === "compare" ? null : ROBOTS.find((item) => item.slug === slug);
  const faqs = robot ? publishedFaqs(robot) : [];
  const pageMeta = usePageMeta({
    title: robot ? detailTitle(robot) : "Page Not Found | MAG",
    description: robot ? summaryDescription(robot.summary) : "The page you're looking for doesn't exist.",
    path: robot ? `/catalog/${robot.slug}` : "/catalog",
    noindex: !robot || !SHOW_EQUIPMENT_CATALOG,
    jsonLd: robot ? equipmentDetailJsonLd(robot, ROBOTS) : undefined,
  });

  if (!robot) return <NotFound />;

  const related = relatedMachines(robot, ROBOTS);
  const applications = (robot.magApplications || []).map(applicationLabel).filter(Boolean);
  const showFits = String(robot.magNotes || "").trim().length > 0;

  return (
    <div className="bg-white text-gray-900">
      {pageMeta}

      <div className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6">
        <div className="grid items-start gap-6 lg:grid-cols-[250px_minmax(0,1fr)]">
        <aside className="lg:sticky lg:top-24 lg:pt-1">
          <Link to="/catalog" className="text-sm text-gray-800 underline decoration-gray-300 underline-offset-4">
            ← Catalog
          </Link>
        </aside>
        <div>
        <section className="relative overflow-hidden border border-gray-200 bg-white lg:min-h-[36rem]">
          <div className="equipment-plate equipment-stage-plate absolute inset-0" aria-hidden="true" />
          <div className="relative z-10 max-w-sm px-5 pt-7 sm:px-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-500">Catalog</p>
            <VendorLogo slug={robot.vendorSlug} className="mt-4 h-8 w-auto max-w-[11rem] object-contain object-left" />
            <h1 className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl">{robot.displayName}</h1>
            <p className="mt-2 text-sm text-gray-600">{robot.typeLabel}</p>
            <p className="mt-4 text-base leading-relaxed text-gray-600">{keyFactsText(robot)}</p>
          </div>

          <div className={`equipment-tone-${robot.vendorSlug} pointer-events-none relative z-10 mx-auto mt-4 h-72 w-72 sm:h-80 sm:w-80 lg:absolute lg:left-[58%] lg:top-1/2 lg:mt-0 lg:h-[22rem] lg:w-[22rem] lg:-translate-x-1/2 lg:-translate-y-1/2`}>
            <span className="equipment-ring equipment-ring-a" aria-hidden="true" />
            <span className="equipment-ring equipment-ring-b" aria-hidden="true" />
            <span className="equipment-ring equipment-ring-c" aria-hidden="true" />
            <img
              src={robot.image.src}
              alt={robot.image.alt}
              width="1200"
              height="900"
              fetchPriority="high"
              className="relative z-10 h-full w-full object-contain"
            />
          </div>

          <div className="equipment-glass relative z-20 mx-4 mb-4 mt-6 p-5 sm:mx-5 lg:absolute lg:bottom-6 lg:right-6 lg:mx-0 lg:mb-0 lg:mt-0 lg:w-[320px]">
            <h2 className="text-sm font-semibold">What are the key figures?</h2>
            <dl className="mt-4 space-y-2 text-sm">
              {HERO_FIGURES.map((figure) => (
                <div key={figure.label} className="flex justify-between gap-4 border-b border-gray-900/10 pb-2 last:border-b-0 last:pb-0">
                  <dt className="text-gray-500">{figure.label}</dt>
                  <dd className="text-right">{figure.value(robot)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <div className="mx-auto max-w-6xl">

        {showFits ? (
          <section className="mt-12">
            <h2 className="text-2xl font-semibold">
              {robot.magDeployed ? "Where has MAG deployed the " + robot.model + "?" : "What this platform is built for"}
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-gray-700">{robot.magNotes}</p>
            {applications.length > 0 ? (
              <ul className="mt-4 list-disc pl-5 text-sm text-gray-700">
                {applications.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}

        <section className="mt-12">
          <h2 className="text-2xl font-semibold">What are the full specifications?</h2>
          <div className="mt-4">
            <EquipmentSpecTable rows={specRows(robot)} />
          </div>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-gray-700">{robot.specNotes}</p>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-gray-700">{CONFIGURATION_DISCLAIMER}</p>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-semibold">When were these specifications verified?</h2>
          <p className="mt-4 text-sm text-gray-700">
            {`Specifications verified ${formatVerifiedDate(robot.specsVerifiedOn)}.`}
          </p>
          <p className="mt-3 text-sm">
            <a href={robot.sourceUrl} className="underline" rel="noopener noreferrer">
              Manufacturer specification
            </a>
          </p>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-semibold">{`Which machines are closest to the ${robot.model}?`}</h2>
          <ul className="mt-4 grid gap-4 sm:grid-cols-3">
            {related.map((item) => (
              <li key={item.slug} className="border border-gray-200 bg-white">
                <Link to={`/catalog/${item.slug}`} className="block p-4">
                  <span className={`equipment-tone-${item.vendorSlug} relative mx-auto block h-40 w-40`}>
                    <span className="equipment-ring equipment-ring-b" aria-hidden="true" />
                    <img
                      src={item.image.src}
                      alt={item.image.alt}
                      width="1200"
                      height="900"
                      loading="lazy"
                      decoding="async"
                      className="relative z-10 h-full w-full object-contain"
                    />
                  </span>
                  <span className="mt-3 block font-semibold">{item.displayName}</span>
                  <span className="mt-1 block text-sm text-gray-600">{item.typeLabel}</span>
                  <span className="mt-2 block text-sm">{displaySpec(item.payloadKg, "kg")}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {faqs.length > 0 ? (
          <section className="mt-12">
            <h2 className="text-2xl font-semibold">What are the common questions?</h2>
            <div className="mt-4 divide-y divide-gray-200 border border-gray-200">
              {faqs.map((faq) => (
                <div key={faq.question} className="px-5 py-4">
                  <h3 className="font-semibold">{faq.question}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="mt-12 border border-gray-200 bg-white px-6 py-10">
          <h2 className="text-2xl font-semibold">{`How the ${robot.model} is commissioned`}</h2>
          <p className="mt-3 max-w-2xl text-gray-600">
            {`The ${robot.model} is matched to the plant, installed with the fleet, and supported after launch.`}
          </p>
          <Link to="/contact" className="btn-primary-dark mt-6 inline-block">
            Contact MAG
          </Link>
        </section>
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}
